import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  wrapperClassName = '',
  fadeInDuration = 0.6,
  threshold = 0.1,
  spinnerLoader = false,
  pulseLoader = false,
  dualRingLoader = false,
  isBanner = false,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: threshold,
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [threshold]);

  const handleError = () => {
    setIsLoaded(true);
    setHasError(true);
  };

  // Determine loader type
  const getLoaderClass = () => {
    if (pulseLoader) return 'image-pulse-loader';
    if (dualRingLoader) return 'image-dual-ring-loader';
    if (spinnerLoader) return 'image-spinner';
    return 'image-loading';
  };

  // Add mobile banner class if needed
  const imageClassName = `${className} ${isBanner ? 'hero-banner-mobile' : ''}`;

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${wrapperClassName}`}>
      {/* Enhanced Loader - shows while image is loading */}
      {!isLoaded && isInView && (
        <div className={`absolute inset-0 ${getLoaderClass()} rounded-lg`}>
          {/* Optional Loading Text for Large Images */}
          {isBanner && (
            <div className="loading-text-indicator">LOADING</div>
          )}
        </div>
      )}

      {/* Actual Image */}
      {isInView && !hasError && (
        <motion.img
          src={src}
          alt={alt}
          className={imageClassName}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ 
            opacity: isLoaded ? 1 : 0,
            scale: isLoaded ? 1 : 1.05
          }}
          transition={{ 
            duration: fadeInDuration,
            ease: 'easeOut'
          }}
          onLoad={() => setIsLoaded(true)}
          onError={handleError}
          loading="lazy"
          {...props}
        />
      )}

      {/* Error Fallback */}
      {hasError && (
        <div className={`${className} flex items-center justify-center bg-muted/50 text-muted-foreground rounded-lg`}>
          <div className="text-center p-4">
            <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-xs">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;
